import { useState, useEffect, useCallback, useMemo } from 'react';
import { getApiUrl } from '../config/api';
const PRODUCTS_CACHE_KEY = 'aerotubos2025_products_cache';
const CACHE_EXPIRY_KEY = 'aerotubos2025_products_cache_expiry';
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes


export const useProducts = (token) => {
  const [products, setProducts] = useState([]);
  const [originalProducts, setOriginalProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [hasChanges, setHasChanges] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [tableLocked, setTableLocked] = useState(false);
  const [editedData, setEditedData] = useState({});
  const [clearEditingState, setClearEditingState] = useState(false);

  // Check if cache is valid
  const isCacheValid = useCallback(() => {
    const expiry = localStorage.getItem(CACHE_EXPIRY_KEY);
    if (!expiry) return false;
    return Date.now() < parseInt(expiry);
  }, []);

  // Load products from cache
  const loadFromCache = useCallback(() => {
    try {
      const cached = localStorage.getItem(PRODUCTS_CACHE_KEY);
      if (cached) {
        const data = JSON.parse(cached);
        // Ensure all products have isHidden field set to false if missing
        const normalizedProducts = data.map(product => ({
          ...product,
          isHidden: product.isHidden === true
        }));
        // Filter out hidden products when loading from cache for regular inventory
        const visibleProducts = normalizedProducts.filter(product => !product.isHidden);
        setProducts(visibleProducts);
        setOriginalProducts(JSON.parse(JSON.stringify(visibleProducts))); // Deep copy
        setEditedData({}); // Clear any previous edits
        setHasChanges(false);
        return true;
      }
    } catch (err) {
      console.error('Error loading from cache:', err);
    }
    return false;
  }, []);

  // Save products to cache
  const saveToCache = useCallback((data) => {
    try {
      localStorage.setItem(PRODUCTS_CACHE_KEY, JSON.stringify(data));
      localStorage.setItem(CACHE_EXPIRY_KEY, (Date.now() + CACHE_DURATION).toString());
    } catch (err) {
      console.error('Error saving to cache:', err);
    }
  }, []);

  // Fetch products from server
  const fetchProducts = useCallback(async (forceRefresh = false) => {
    if (!token) return;

    // Always try cache first if not forcing refresh
    if (!forceRefresh && loadFromCache()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(getApiUrl('/api/products'), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        // Ensure all products have isHidden field set to false if missing
        const normalizedProducts = data.map(product => ({
          ...product,
          isHidden: product.isHidden === true
        }));
        // Filter out hidden products when loading from server
        const visibleProducts = normalizedProducts.filter(product => !product.isHidden);
        setProducts(visibleProducts);
        setOriginalProducts(JSON.parse(JSON.stringify(visibleProducts))); // Deep copy
        setEditedData({}); // Clear any previous edits
        setHasChanges(false);
        // Save all products (including hidden ones) to cache
        saveToCache(normalizedProducts);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch products');
      }
    } catch (err) {
      setError('Network error. Please check your connection.');
    } finally {
      setLoading(false);
    }
  }, [token, loadFromCache, saveToCache]);

  // Load products on mount and when token changes
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Update product field
  const updateProduct = useCallback((productId, field, value) => {
    if (tableLocked) return; // Prevent updates when table is locked
    
    console.log('updateProduct called:', { productId, field, value });
    
    // Update the products array for immediate UI feedback
    setProducts(prevProducts => {
      const updated = prevProducts.map(product => {
        if (product.productId === productId) {
          return { ...product, [field]: value };
        }
        return product;
      });
      
      return updated;
    });
    
    // Update editedData to track changes
    setEditedData(prev => {
      const newEditedData = {
        ...prev,
        [productId]: {
          ...prev[productId],
          [field]: value
        }
      };
      
      // Check if this product now matches its original state
      const originalProduct = originalProducts.find(p => p.productId === productId);
      if (originalProduct) {
        // Create a merged product with all current edits plus the new value
        const currentEdits = newEditedData[productId] || {};
        const mergedProduct = { ...originalProduct, ...currentEdits, [field]: value };
        
        // Check if all fields match the original (considering default values)
        const isMatchingOriginal = Object.keys(originalProduct).every(key => {
          const originalValue = originalProduct[key];
          const currentValue = mergedProduct[key];
          
          // Handle isHidden field specially - if it's missing, treat as false
          if (key === 'isHidden') {
            const originalHidden = originalValue === true;
            const currentHidden = currentValue === true;
            return originalHidden === currentHidden;
          }
          
          return originalValue === currentValue;
        });
        
        // Also check if any new fields (not in original) have been added
        const hasNewFields = Object.keys(mergedProduct).some(key => 
          !originalProduct.hasOwnProperty(key) && mergedProduct[key] !== undefined && mergedProduct[key] !== ''
        );
        
        // If it matches the original AND has no new fields, remove from editedData
        if (isMatchingOriginal && !hasNewFields) {
          const { [productId]: removed, ...rest } = newEditedData;
          console.log('Product matches original, removing from editedData');
          return rest;
        }
      } else {
        // If no original product found, this is a new product - always keep in editedData
        console.log('New product detected, keeping in editedData');
      }
      
      console.log('New editedData:', newEditedData);
      return newEditedData;
    });
  }, [tableLocked, originalProducts]);

  // Check for changes based on editedData
  useEffect(() => {
    const hasChanges = Object.keys(editedData).length > 0;
    setHasChanges(hasChanges);
  }, [editedData]);

  // Save changes to server
  const saveChanges = useCallback(async () => {
    if (!token || !hasChanges || Object.keys(editedData).length === 0) return;

    setTableLocked(true);
    setUpdating(true);
    setError(null);

    try {
      // Send only the edited products to the server
      const editedProducts = Object.keys(editedData).map(id => {
        const product = products.find(p => p.productId === id);
        return { ...product, ...editedData[id] };
      });

      const response = await fetch(getApiUrl('/api/products'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ products: editedProducts })
      });

      if (response.ok || response.status === 201) {
        const data = await response.json();
        // Filter out hidden products after successful save
        const visibleProducts = products.filter(product => !product.isHidden);
        setProducts(visibleProducts);
        setOriginalProducts(JSON.parse(JSON.stringify(visibleProducts)));
        setEditedData({}); // Clear edited data
        setHasChanges(false);
        
        // Update cache with all products (including hidden ones)
        // We need to fetch all products again to get the updated data from server
        const allProductsResponse = await fetch(getApiUrl('/api/products'), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        if (allProductsResponse.ok) {
          const allProductsData = await allProductsResponse.json();
          const normalizedAllProducts = allProductsData.map(product => ({
            ...product,
            isHidden: product.isHidden === true
          }));
          saveToCache(normalizedAllProducts);
        }
        
        // Trigger clearing of editing state in ProductTable
        setClearEditingState(prev => !prev);
        return { success: true, message: data.message || 'Changes saved successfully' };
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save changes');
        return { success: false, error: errorData.error };
      }
    } catch (err) {
      const errorMessage = 'Network error. Please check your connection.';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setUpdating(false);
      setTableLocked(false);
    }
  }, [token, hasChanges, editedData, products, saveToCache]);

  // Revert a single product field without tracking as a change
  const revertProduct = useCallback((productId, field, originalValue) => {
    if (tableLocked) return; // Prevent revert when table is locked
    
    // Update the products array directly without adding to editedData
    setProducts(prevProducts => {
      return prevProducts.map(product => {
        if (product.productId === productId) {
          return { ...product, [field]: originalValue };
        }
        return product;
      });
    });
  }, [tableLocked]);

  // Undo changes
  const undoChanges = useCallback(() => {
    if (tableLocked) return; // Prevent undo when table is locked
    setProducts(JSON.parse(JSON.stringify(originalProducts))); // Deep copy
    setEditedData({}); // Clear edited data
    setHasChanges(false);
  }, [originalProducts, tableLocked]);

  // Search and filter products
  const filteredProducts = useMemo(() => {
    if (!searchTerm || searchTerm.trim() === '') return products;

    const term = searchTerm.toLowerCase().trim();
    return products.filter(product => 
      Object.values(product).some(value => 
        String(value).toLowerCase().includes(term)
      )
    );
  }, [products, searchTerm]);

  // Sort products
  const sortedProducts = useMemo(() => {
    if (!sortField) return filteredProducts;

    return [...filteredProducts].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      // Handle null/undefined values
      if (aVal === null || aVal === undefined) aVal = '';
      if (bVal === null || bVal === undefined) bVal = '';

      // Handle different data types
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }

      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredProducts, sortField, sortDirection]);

  // Handle sorting
  const handleSort = useCallback((field) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  }, [sortField]);

  // Get sort indicator for a field
  const getSortIndicator = useCallback((field) => {
    if (sortField !== field) return '↕';
    return sortDirection === 'asc' ? '▲' : '▼';
  }, [sortField, sortDirection]);

  // Check if product is low stock
  const isLowStock = useCallback((stock) => {
    return stock < 20;
  }, []);

  // Format price as currency
  const formatPrice = useCallback((price) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(price);
  }, []);

  return {
    products: sortedProducts,
    filteredProducts: sortedProducts,
    loading,
    error,
    searchTerm,
    setSearchTerm,
    hasChanges,
    updating,
    tableLocked,
    editedData,
    clearEditingState,
    updateProduct,
    revertProduct,
    saveChanges,
    undoChanges,
    handleSort,
    getSortIndicator,
    isLowStock,
    formatPrice,
    refetch: fetchProducts,
    refreshFromServer: () => fetchProducts(true)
  };
};
